import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck, Eraser, UserCog, KeyRound, Copy, Trash2, UserPlus, Send, RotateCcw } from "lucide-react";

export default function SVJSettings({ svj, isSuper, reload }) {
  const [info, setInfo] = useState(svj.operator_info || "");
  const [contact, setContact] = useState(svj.resident_contact || "");
  const [days, setDays] = useState(svj.retention_days || 365);
  const [ret, setRet] = useState(null);
  const [saving, setSaving] = useState(false);
  const loadRet = useCallback(() => api.get(`/svjs/${svj.id}/retention-status`).then((r) => setRet(r.data)).catch(() => {}), [svj.id]);
  useEffect(() => { loadRet(); }, [loadRet]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/svjs/${svj.id}`, { operator_info: info, retention_days: parseInt(days, 10) || 365, resident_contact: contact });
      toast.success("Nastavení uloženo"); reload?.(); loadRet();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const anonymize = async () => {
    if (!window.confirm("Anonymizovat kontakty všech rezervací starších než doba uchování? Akce je nevratná.")) return;
    try { const { data } = await api.post(`/svjs/${svj.id}/anonymize`); toast.success(`Anonymizováno ${data.anonymized} rezervací`); loadRet(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mt-4 max-w-2xl space-y-4">
      <ChairmanAccess svj={svj} reload={reload} isSuper={isSuper} />
      <div className="bg-white rounded-xl border p-6 space-y-4">
        <h3 className="font-semibold text-[#0A2540] flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Zpracování údajů a kontakt provozovatele</h3>
        <p className="text-sm text-slate-500">Tento text se zobrazuje obyvatelům ve veřejném rezervačním formuláři (sekce „Informace o zpracování osobních údajů“). Uveďte správce údajů, IČO, kontaktní e-mail/telefon a případně odkaz na zásady.</p>
        <Textarea rows={6} value={info} onChange={(e) => setInfo(e.target.value)} placeholder={"Správce údajů: OKENFIX s.r.o., IČO 12345678\nKontakt: gdpr@okenfix.cz, +420 …"} data-testid="settings-operator-info" />
        <div className="space-y-1">
          <Label className="text-xs">Kontakt pro obyvatele (zobrazí se v rezervaci, po potvrzení, v e-mailech a letáku)</Label>
          <Textarea rows={4} value={contact} onChange={(e) => setContact(e.target.value)} placeholder={`Prázdné = jméno a telefon předsedy (${svj.chairman_name || ""} ${svj.chairman_phone || ""})\nnapř.:\nJosef Novák – 601 000 000\nJana Nová – 602 000 000`} data-testid="settings-resident-contact" />
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1"><Label className="text-xs">Doba uchování kontaktů (dny)</Label><Input type="number" min="7" max="3650" value={days} onChange={(e) => setDays(e.target.value)} className="w-32" data-testid="settings-retention-days" /></div>
          <Button onClick={save} disabled={saving} style={{ background: "#0A2540" }} data-testid="settings-save">Uložit</Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6 space-y-3">
        <h3 className="font-semibold text-[#0A2540] flex items-center gap-2"><Eraser className="h-4 w-4" /> Automatická anonymizace</h3>
        <p className="text-sm text-slate-500">Každý den ve 3:00 systém vymaže jméno, telefon, e-mail a poznámku u rezervací, jejichž termín je starší než nastavená doba uchování. Historie rezervací (byt, termín, stav) zůstává zachována.</p>
        {ret && (
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Box label="Doba uchování" value={`${ret.retention_days} dní`} />
            <Box label="Čeká na anonymizaci" value={ret.due} testId="retention-due" />
            <Box label="Již anonymizováno" value={ret.anonymized} testId="retention-done" />
          </div>
        )}
        {isSuper && <Button variant="outline" onClick={anonymize} data-testid="anonymize-now">Spustit anonymizaci nyní</Button>}
      </div>

      {isSuper && <ResetBox svj={svj} reload={reload} />}
    </div>
  );
}

function ChairmanRow({ svj, c, onChange, isSuper }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); setPwd(""); onChange(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Zkopírováno"); };
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid={`chairman-row-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-[#0A2540]">{c.name}</span>
        <span className="text-slate-600">{c.email}</span>
        <span className={`px-2 py-0.5 rounded text-xs ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid={`chairman-status-${c.id}`}>{c.active ? "Aktivní" : "Čeká na aktivaci"}</span>
        <div className="ml-auto flex items-center gap-1">
          <Input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="nové heslo (min. 8)" className="h-8 w-44 text-xs" data-testid={`chairman-pwd-${c.id}`} />
          <Button size="sm" variant="outline" className="h-8" disabled={busy || pwd.length < 8} onClick={() => run(() => api.patch(`/svjs/${svj.id}/chairmen/${c.id}`, { password: pwd }), "Heslo nastaveno")} data-testid={`chairman-setpwd-${c.id}`}><KeyRound className="h-3 w-3" /></Button>
          {isSuper && <Button size="sm" variant="ghost" className="h-8 text-red-600" disabled={busy} onClick={() => window.confirm(`Odebrat předsedu ${c.email}? Přístup do systému mu bude zrušen.`) && run(() => api.delete(`/svjs/${svj.id}/chairmen/${c.id}`), "Předseda odebrán")} data-testid={`chairman-remove-${c.id}`}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>
      {c.invite_link && !c.active && (
        <div className="bg-[#FDF8EC] border border-[#C5A059] rounded-lg p-2 flex items-center gap-2 text-xs">
          <span className="text-slate-600 shrink-0">Pozvánka (14 dní):</span>
          <code className="flex-1 break-all" data-testid={`chairman-invite-${c.id}`}>{c.invite_link}</code>
          <Button size="sm" variant="outline" className="h-7" onClick={() => copy(c.invite_link)} data-testid={`chairman-copy-${c.id}`}><Copy className="h-3 w-3" /></Button>
          <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={() => run(() => api.post(`/svjs/${svj.id}/chairmen/${c.id}/invite`), "Pozvánka odeslána e-mailem")} data-testid={`chairman-send-invite-${c.id}`}><Send className="h-3 w-3 mr-1" /> Poslat e-mailem</Button>
        </div>
      )}
    </div>
  );
}

function ChairmanAccess({ svj, reload, isSuper }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ email: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/svjs/${svj.id}/chairmen`).then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e))), [svj.id]);
  useEffect(() => { load(); }, [load]);
  const onChange = () => { load(); reload?.(); };
  const add = async () => {
    if (f.password && f.password.length < 8) return toast.error("Heslo musí mít alespoň 8 znaků");
    setBusy(true);
    try {
      const body = { email: f.email, name: f.name };
      if (f.password) body.password = f.password;
      const { data } = await api.post(`/svjs/${svj.id}/chairmen`, body);
      setF({ email: "", name: "", password: "" }); onChange();
      toast.success(data.active ? "Předseda přidán – může se přihlásit" : "Předseda přidán – pozvánka odeslána e-mailem");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl border p-6 space-y-4" data-testid="chairman-access">
      <h3 className="font-semibold text-[#0A2540] flex items-center gap-2"><UserCog className="h-4 w-4" /> Předsedové / správci SVJ ({list.length})</h3>
      <div className="space-y-2" data-testid="chairman-list">
        {list.map((c) => <ChairmanRow key={c.id} svj={svj} c={c} onChange={onChange} isSuper={isSuper} />)}
        {list.length === 0 && <p className="text-sm text-amber-700" data-testid="chairman-empty">SVJ nemá žádného předsedu s přístupem.</p>}
      </div>
      <div className="border-t pt-4 space-y-2">
        <div className="text-sm font-medium text-[#0A2540] flex items-center gap-1"><UserPlus className="h-4 w-4" /> Přidat předsedu</div>
        <div className="grid sm:grid-cols-3 gap-2">
          <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="e-mail (přihlašovací)" data-testid="chairman-email-input" />
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="jméno" data-testid="chairman-name-input" />
          <Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="heslo (volitelné, jinak pozvánka)" data-testid="chairman-password-input" />
        </div>
        <Button onClick={add} disabled={busy || !f.email} style={{ background: "#0A2540" }} data-testid="chairman-add-btn"><UserPlus className="h-4 w-4 mr-1" /> Přidat předsedu</Button>
      </div>
      <p className="text-xs text-slate-500">Předsedové se přihlašují na stejné adrese jako vy a vidí pouze toto SVJ. Nově pozvaný předseda dostane e-mail s odkazem pro nastavení hesla. Odebrat předsedu může pouze OKENFIX (superadmin). Schvalovací e-maily chodí na kontaktní e-mail SVJ (první předseda).</p>
    </div>
  );
}

function ResetBox({ svj, reload }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = confirm.trim() === svj.name;
  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/svjs/${svj.id}/reset`);
      toast.success(`${data.svj}: smazáno ${data.reservations_deleted} rezervací, uvolněno ${data.slots_freed} termínů`);
      setConfirm(""); reload?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-red-200 p-6 space-y-3" data-testid="reset-box">
      <h3 className="font-semibold text-red-700 flex items-center gap-2"><RotateCcw className="h-4 w-4" /> {svj.is_demo ? "Reset demo dat" : "Vrátit SVJ do původní podoby"}</h3>
      <p className="text-sm text-slate-500">Smaže <b>všechny rezervace, zápisy techniků, návrhy víceprací a fotky</b> tohoto SVJ a uvolní všechny termíny. Struktura domu, jména vlastníků, akce, harmonogram, přístup obyvatel i účty předsedů zůstanou. Akce je nevratná.</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1"><Label className="text-xs">Pro potvrzení opište název SVJ: <b>{svj.name}</b></Label><Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={svj.name} className="w-64" data-testid="reset-confirm-input" /></div>
        <Button variant="destructive" disabled={!ok || busy} onClick={reset} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-1" /> {svj.is_demo ? "Resetovat demo" : "Vrátit do původní podoby"}</Button>
      </div>
    </div>
  );
}

function Box({ label, value, testId }) {
  return <div className="bg-slate-50 rounded-lg p-3"><div className="text-xl font-bold text-[#0A2540]" data-testid={testId}>{value}</div><div className="text-xs text-slate-500">{label}</div></div>;
}
